import {ISupplierDetailEntry} from '../models/supplier-code-auto-fill.interface';

/**
 * Returns whether the given supplier code appears in the item's other-suppliers list.
 */
export function isSupplierInMultipleSuppliers(supplierCode: string, multipleSuppliers: string[] | undefined): boolean {
  const normalizedCode = normalizeComparableCode(supplierCode);
  if (!normalizedCode || !multipleSuppliers?.length) {
    return false;
  }
  return multipleSuppliers.some((code: string) => normalizeComparableCode(code) === normalizedCode);
}

/**
 * Normalizes Firestore supplierDetails stored as an array or keyed map into a flat list.
 * Map keys are treated as supplier codes when nested entries omit `supplier`.
 */
export function normalizeSupplierDetails(details: unknown): ISupplierDetailEntry[] {
  if (!details) {
    return [];
  }
  if (Array.isArray(details)) {
    return details
      .map((entry: unknown) => toSupplierDetailEntry(entry))
      .filter((entry: ISupplierDetailEntry | null): entry is ISupplierDetailEntry => entry !== null);
  }
  if (typeof details === 'object') {
    return Object.entries(details as Record<string, unknown>)
      .map(([key, value]: [string, unknown]) => toSupplierDetailEntry(value, key))
      .filter((entry: ISupplierDetailEntry | null): entry is ISupplierDetailEntry => entry !== null);
  }
  return [];
}

/**
 * Finds the supplier stock code for a supplier from supplierDetails.
 */
export function findStockCodeForSupplier(supplierCode: string, details: unknown): string | null {
  const normalizedCode = normalizeComparableCode(supplierCode);
  if (!normalizedCode) {
    return null;
  }
  const match = normalizeSupplierDetails(details).find(
    (entry: ISupplierDetailEntry) => normalizeComparableCode(entry.supplier) === normalizedCode,
  );
  const stockCode = match?.stockCode?.trim();
  return stockCode ? stockCode : null;
}

function normalizeComparableCode(value: string | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function toSupplierDetailEntry(value: unknown, fallbackSupplier?: string): ISupplierDetailEntry | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const supplierFromEntry = typeof record['supplier'] === 'string' ? record['supplier'].trim() : '';
  const supplier = supplierFromEntry || String(fallbackSupplier ?? '').trim();
  if (!supplier) {
    return null;
  }

  const rawStockCode = record['stockCode'];
  const stockCode = rawStockCode === undefined || rawStockCode === null
    ? undefined
    : String(rawStockCode).trim();

  return {
    supplier,
    stockCode: stockCode || undefined,
  };
}
